/** Pure pixel-geometry helpers for the one-shot effects (unit-tested; no Phaser). */
import type { SeededRng } from '@/core/rng';

export interface Pt { x: number; y: number }

export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - clamp(t, 0, 1), 3);
export const easeInOutSine = (t: number): number => 0.5 - 0.5 * Math.cos(Math.PI * clamp(t, 0, 1));

/** `n` points evenly spaced on an ellipse, first one at `startAngle` (default: top). */
export function ellipsePoints(cx: number, cy: number, rx: number, ry: number, n: number, startAngle = -Math.PI / 2): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < n; i++) { const a = startAngle + (i / n) * Math.PI * 2; out.push({ x: Math.round(cx + Math.cos(a) * rx), y: Math.round(cy + Math.sin(a) * ry) }); }
  return out;
}

/** The five stakes of the ritual (3/4 view: the circle is squashed vertically). */
export function pentagonPoints(cx: number, cy: number, r: number, squash = 0.55): Pt[] {
  return ellipsePoints(cx, cy, r, r * squash, 5);
}

/** Every unordered pair (i < j) of `n` items — "两两连接": 10 lines for 5 stakes. */
export function allPairs(n: number): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) out.push([i, j]);
  return out;
}

/**
 * Index of the line that "points southwest" (screen coordinates: left + down). A line has two
 * directions, so the better of the two is scored against the SW unit vector.
 */
export function southwestLineIndex(pts: readonly Pt[], pairs: readonly [number, number][]): number {
  const sw = { x: -Math.SQRT1_2, y: Math.SQRT1_2 };
  let best = 0, bestScore = -Infinity, bestLen = 0;
  pairs.forEach(([a, b], i) => {
    const pa = pts[a], pb = pts[b]; if (!pa || !pb) return;
    const dx = pb.x - pa.x, dy = pb.y - pa.y; const len = Math.hypot(dx, dy) || 1;
    const score = Math.abs((dx / len) * sw.x + (dy / len) * sw.y);
    if (score > bestScore + 1e-6 || (Math.abs(score - bestScore) <= 1e-6 && len > bestLen)) {
      bestScore = score; best = i; bestLen = len;
    }
  });
  return best;
}

/** Bresenham raster of the segment (inclusive of both ends), integer pixels. */
export function bresenham(x0: number, y0: number, x1: number, y1: number): Pt[] {
  let ax = Math.round(x0), ay = Math.round(y0); const bx = Math.round(x1), by = Math.round(y1);
  const dx = Math.abs(bx - ax), dy = -Math.abs(by - ay); const sx = ax < bx ? 1 : -1, sy = ay < by ? 1 : -1;
  let err = dx + dy; const out: Pt[] = [];
  for (let guard = 0; guard < 4096; guard++) {
    out.push({ x: ax, y: ay });
    if (ax === bx && ay === by) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; ax += sx; }
    if (e2 <= dx) { err += dx; ay += sy; }
  }
  return out;
}

/** Upright triangle on the floor (3/4 view): apex up, base below, vertically squashed. */
export function trianglePoints(cx: number, cy: number, size: number, squash = 0.6): [Pt, Pt, Pt] {
  const h = size * squash;
  return [
    { x: Math.round(cx), y: Math.round(cy - h * 0.6) },
    { x: Math.round(cx - size * 0.58), y: Math.round(cy + h * 0.4) },
    { x: Math.round(cx + size * 0.58), y: Math.round(cy + h * 0.4) },
  ];
}

/** `n` points along an arc from angle a0 to a1 (radians, screen space). */
export function arcPoints(cx: number, cy: number, r: number, a0: number, a1: number, n: number): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i < n; i++) { const a = n === 1 ? a0 : lerp(a0, a1, i / (n - 1)); out.push({ x: Math.round(cx + Math.cos(a) * r), y: Math.round(cy + Math.sin(a) * r) }); }
  return out;
}

export const FACING_ANGLE: Record<string, number> = { right: 0, down: Math.PI / 2, left: Math.PI, up: -Math.PI / 2 };

export interface GlitchBurst { at: number; dur: number; amount: number }
/**
 * Deterministic schedule of pixelation bursts for `ms` of omen glitch at `strength` (0..1): bursts get
 * denser and harder towards the end (the unease "crawls up the spine"). Never overlaps, never past `ms`.
 */
export function glitchSchedule(rng: SeededRng, ms: number, strength: number): GlitchBurst[] {
  const s = clamp(strength, 0, 1); const count = Math.max(1, Math.round(2 + s * 7));
  const out: GlitchBurst[] = []; let t = ms * 0.12;
  for (let i = 0; i < count && t < ms; i++) {
    const u = i / Math.max(1, count - 1);
    const dur = Math.round(40 + rng.range(0, 90) + u * 80);
    const amount = Math.round(1 + u * (1 + s * 3) + rng.range(0, 1));
    if (t + dur > ms) break;
    out.push({ at: Math.round(t), dur, amount });
    t += dur + Math.max(60, (ms - t) / Math.max(1, count - i) * rng.range(0.6, 1.1));
  }
  return out;
}

/** Lerp two 0xRRGGBB colours. */
export function lerpColor(a: number, b: number, t: number): number {
  const k = clamp(t, 0, 1);
  const ar = (a >> 16) & 255, ag = (a >> 8) & 255, ab = a & 255, br = (b >> 16) & 255, bg = (b >> 8) & 255, bb = b & 255;
  return (Math.round(ar + (br - ar) * k) << 16) | (Math.round(ag + (bg - ag) * k) << 8) | Math.round(ab + (bb - ab) * k);
}

/** Parse '#rrggbb' / 'rrggbb' / number into 0xRRGGBB (fallback for junk). */
export function parseColor(v: unknown, fallback: number): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v & 0xffffff;
  if (typeof v === 'string') { const m = /^#?([0-9a-f]{6})$/i.exec(v.trim()); if (m) return parseInt(m[1] as string, 16); }
  return fallback;
}
